import { Component, input, signal } from '@angular/core';
import { AngularModule } from '../../../modules/angular.module';
import { ZorroModule } from '../../../modules/zorro.module';
import { NavigationService } from '../../../services/navigation.service';
import { LoginFormComponent } from '../../forms/standard/login-form/login-form.component';
import { AuthService } from '../../../services/auth.service';
import { CreateAccountFormComponent } from '../../forms/standard/create-account-form/create-account-form.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ForgotPasswordFormComponent } from '../../forms/standard/forgot-password-form/forgot-password-form.component';
import { ConfirmForgotPasswordFormComponent } from '../../forms/standard/confirm-forgot-password-form/confirm-forgot-password-form.component';
import { Subscription } from 'rxjs';
import {
	CognitoCreateEmailAccountResponse,
	CognitoCreateStudentIdAccountResponse,
	HomeState,
	OneTimePasswordExchangeSource,
	TokenPack,
	UserUtils,
} from '@mira-avant/mtlibs';
import { GradientBgComponent } from '../../ui/gradient-bg/gradient-bg.component';
import { VerifyAccountFormComponent } from '../../forms/standard/verify-account-form/verify-account-form.component';
import { UrlHelper } from '../../../utils/url.helper';
import { ChangeTemporaryPasswordFormComponent } from '../../forms/standard/change-temporary-password-form/change-temporary-password-form.component';
import { CognitoService } from '../../../services/cognito.service';
import { ResetPasswordFormComponent } from '../../forms/standard/reset-password-form/reset-password-form.component';
import { LoggedInService } from '../../../services/logged-in.service';
import { ActivatedRoute } from '@angular/router';

@Component({
	selector: 'app-home',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		LoginFormComponent,
		CreateAccountFormComponent,
		ForgotPasswordFormComponent,
		ConfirmForgotPasswordFormComponent,
		GradientBgComponent,
		VerifyAccountFormComponent,
		ChangeTemporaryPasswordFormComponent,
		ResetPasswordFormComponent,
	],
	templateUrl: './home.component.html',
	styleUrl: './home.component.scss',
})
export class HomeComponent {
	readonly state = signal<HomeState>(HomeState.LOGIN);
	readonly loading = signal<boolean>(false);
	readonly isAdmin = signal<boolean>(false);
	readonly lastEmailEntered = signal<string>('');
	readonly lastStudentId = signal<string | undefined>(undefined);
	readonly setupCode = signal<string | undefined>(undefined);
	readonly setupLinkExpired = signal<boolean>(false);
	readonly setupLinkValidating = signal<boolean>(false);
	readonly setupLinkValidationFailed = signal<boolean>(false);
	readonly signupLinkResend = signal<boolean>(false);
	readonly UserUtils = UserUtils;
	readonly HomeState = HomeState;
	private sub: Subscription | undefined;

	constructor(
		public nav: NavigationService,
		public authService: AuthService,
		private cognitoService: CognitoService,
		private message: NzMessageService,
		public loggedInService: LoggedInService,
		private route: ActivatedRoute
	) {
		// One-time check — still using promise → signal
		this.authService.isAdmin$().then(admin => {
			this.isAdmin.set(admin);
		});
	}

	async ngOnInit() {
		const stateParam = this.route.snapshot.paramMap.get('state');

		if (stateParam && Object.values(HomeState).includes(stateParam as HomeState)) {
			this.state.set(stateParam as HomeState);
		}

		await this.validateAccountSetupLink();

		this.sub = this.loggedInService.isLoggedIn$.subscribe(isLoggedIn => {
			// Only override if no route param was used
			if (!stateParam) {
				this.state.set(isLoggedIn ? HomeState.LOGGED_IN : HomeState.LOGIN);
			}

			if (isLoggedIn) {
				this.handleLoggedIn();
			}
		});
	}

	public onLogin($event: any): void {
		this.loading.set(true);

		this.authService
			.checkForChallenge($event.username, $event.password)
			.then(
				async (result: TokenPack) => {
					if (result.challengeName) {
						switch (result.challengeName) {
							case 'RESET_PASSWORD_REQUIRED':
								this.message.success('Password reset required');
								this.state.set(HomeState.RESET_PASSWORD_REQUIRED);
								return;
							case 'NEW_PASSWORD_REQUIRED':
								if (result.setupCode) {
									this.setupCode.set(result.setupCode);
									this.setupLinkExpired.set(false);
									this.state.set(HomeState.CHANGE_TEMPORARY_PASSWORD);
								} else {
									this.message.error(
										'Open the secure sign-up link from your invitation email to set your password.'
									);
								}
								return;
							default:
								console.error('Unknown challenge name', result.challengeName);
								this.message.error('Unknown challenge name');
								return;
						}
					}

					try {
						const loginResult: TokenPack = await this.authService.login($event.username, $event.password);

						if (!loginResult.success) {
							this.message.error(loginResult.error || 'Login failed');
							return;
						}

						if (!loginResult.token || !loginResult.cognitoUser) {
							this.message.error('Invalid login response');
							return;
						}

						this.message.success('Login successful');
						this.state.set(HomeState.LOGGED_IN);
					} catch (error) {
						console.error('Login processing failed', error);
						this.message.error('Unable to login. Please check your credentials and try again.');
					}
				},
				(error: unknown) => {
					console.error('Login failed', error);
					this.message.error('Login failed. Please check your credentials and try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	public onChangedTemporaryPassword($event: any): void {
		this.loading.set(true);

		const subdomains = UrlHelper.getLeftMostSubDomains();
		const code = $event.code || this.setupCode();
		const source =
			$event.source ||
			(code && this.setupCode() ? OneTimePasswordExchangeSource.ACCOUNT_SETUP : undefined);
		const request = code && source
			? {
					code,
					source,
					newPassword: $event.newPassword,
					subdomain: subdomains,
				}
			: undefined;

		if (!request) {
			this.loading.set(false);
			this.message.error('Password setup link is invalid or expired. Please request a new link.');
			return;
		}

		this.cognitoService
			.changeTemporaryPassword(request)
			.then(
				() => {
					this.setupCode.set(undefined);

					this.message.success('Password set successfully. You are now logged in.');
					this.state.set(HomeState.LOGGED_IN);
				},
				(error: unknown) => {
					console.error('Failed to change temporary password', error);
					if (source === OneTimePasswordExchangeSource.ACCOUNT_SETUP && this.hasHttpStatus(error, 404)) {
						this.setupLinkExpired.set(true);
						return;
					}
					this.message.error('Failed to change temporary password. Please try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	private async handleLoggedIn(): Promise<void> {
		this.nav.navigate(['/my', 'journey']);
	}

	public async onCreateAccount($event: any): Promise<void> {
		this.loading.set(true);

		const subdomains = UrlHelper.getLeftMostSubDomains();

		this.authService
			.createAccount(subdomains, $event.username, $event.sectionCodes, $event.firstName, $event.lastName)
			.then(
				async (response: CognitoCreateEmailAccountResponse | CognitoCreateStudentIdAccountResponse) => {
					this.message.success(
						'setupCode' in response
							? 'Account created successfully. Set your password to finish setup.'
							: 'Account created successfully. Please check your email.'
					);

					this.lastEmailEntered.set($event.username);

					this.setupCode.set(
						'setupCode' in response
							? response.setupCode
							: undefined
					);
					this.lastStudentId.set('studentId' in response ? response.studentId : undefined);

					this.state.set(
						UserUtils.isUsernameEmail($event.username)
							? HomeState.CHECK_EMAIL
							: HomeState.CHANGE_TEMPORARY_PASSWORD
					);
				},
				(error: unknown) => {
					console.error('Account creation failed', error);
					this.message.error('Account creation failed. Please try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	public onVerifyAccount($event: any): void {
		this.loading.set(true);

		this.authService
			.verifyAccount($event.username, $event.verificationCode)
			.then(
				() => {
					this.message.success('Account verified successfully. Please login.');
					this.state.set(HomeState.LOGIN);
				},
				(error: unknown) => {
					console.error('Account verification failed', error);
					this.message.error('Account verification failed. Please try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	public onResetPassword($event: any): void {
		this.loading.set(true);

		this.authService
			.resetPassword($event.username, $event.verificationCode, $event.password)
			.then(
				() => {
					this.message.success('Password reset successfully. Please login.');
					this.state.set(HomeState.LOGIN);
				},
				(error: unknown) => {
					console.error('Password reset failed', error);
					this.message.error('Password reset failed. Please try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	public onForgotPassword($event: any): void {
		this.loading.set(true);
		this.lastEmailEntered.set($event.email);

		this.authService
			.forgotPassword($event.email)
			.then(
				() => {
					this.message.success(
						this.signupLinkResend()
							? 'A new sign-up link has been sent if the account is eligible.'
							: 'Password reset initiated. Please check your email for further instructions.'
					);
					this.state.set(HomeState.CHECK_EMAIL);
				},
				(error: unknown) => {
					console.error('Password reset initiation failed', error);
					this.message.error('Failed to initiate password reset. Please try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	public onConfirmForgotPassword($event: any): void {
		this.loading.set(true);

		const source = Object.values(OneTimePasswordExchangeSource).find(value => value === $event.source);

		if (!$event.code || !source || !$event.newPassword) {
			this.loading.set(false);
			this.message.error('Password reset link is invalid or expired. Please request a new link.');
			return;
		}

		this.cognitoService
			.changeTemporaryPassword({
				code: $event.code,
				source,
				newPassword: $event.newPassword,
				subdomain: UrlHelper.getLeftMostSubDomains(),
			})
			.then(
				() => {
					this.message.success('Password reset successfully. Please log in with your new password.');
					this.state.set(HomeState.LOGIN);
				},
				(error: unknown) => {
					console.error('Password reset confirmation failed', error);
					this.message.error('Failed to reset password. Please check your confirmation code and try again.');
				}
			)
			.finally(() => {
				this.loading.set(false);
			});
	}

	public switchState(state: unknown): void {
		const nextState = state as HomeState;
		this.state.set(nextState);
		this.signupLinkResend.set(
			nextState === HomeState.FORGOT_PASSWORD && this.setupLinkExpired()
		);
		if (nextState === HomeState.LOGIN) {
			this.setupLinkExpired.set(false);
		}
	}

	public retryAccountSetupLinkValidation(): void {
		void this.validateAccountSetupLink();
	}

	private async validateAccountSetupLink(): Promise<void> {
		const code = UrlHelper.queryString('code');
		const source = Object.values(OneTimePasswordExchangeSource).find(
			value => value === UrlHelper.queryString('source')
		);

		if (
			this.state() !== HomeState.CHANGE_TEMPORARY_PASSWORD ||
			!code ||
			source !== OneTimePasswordExchangeSource.ACCOUNT_SETUP
		) {
			return;
		}

		this.setupLinkValidating.set(true);
		this.setupLinkValidationFailed.set(false);
		try {
			const isValid = await this.cognitoService.isOneTimePasswordCodeValid(code, source);
			this.setupLinkExpired.set(!isValid);
		} catch (error: unknown) {
			console.error('Failed to validate sign-up link', error);
			this.setupLinkValidationFailed.set(true);
		} finally {
			this.setupLinkValidating.set(false);
		}
	}

	private hasHttpStatus(error: unknown, status: number): boolean {
		return typeof error === 'object' && error !== null && 'status' in error && error.status === status;
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
